import api from "../api";
import normaliseForm from "../../normalisers/form";
import { AxiosRequestConfig } from "axios";

// API path to the forms service
const path = "forms";

/**
 * Create a form instance
 *
 * @param formDefinitionId form definition id
 * @param entityId optional entity id to bind the form to
 */
const create = async (
	formDefinitionId: string,
	entity: string | null = null
) => {
	const formInstance = await api.request({
		method: "POST",
		url: `${path}/instances`,
		data: {
			formDefinitionId,
			entity,
		},
	});

	return normaliseForm(formInstance.data);
};

interface ChildConfig {
	element: string;
	bindingIndex: number;
	index?: number;
}

const createChild = async (
	instanceId: string,
	config: ChildConfig,
	newRecord: boolean,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const response = await api.request({
		method: "POST",
		url: `${path}/instances/${instanceId}/child${
			newRecord ? "?new=true" : ""
		}`,
		data: config,
		customOpts,
	});

	return normaliseForm(response.data);
};

/**
 * Get a form instance
 *
 * @param instanceId form instance id
 */
const get = async (instanceId: string) => {
	const formInstance = await api.request({
		method: "GET",
		url: `${path}/instances/${instanceId}`,
	});

	return normaliseForm(formInstance.data);
};

/**
 * Validate a form instance
 *
 * @param instanceId form instance id
 */
const validateForm = async (instanceId: string) => {
	const formInstance = await api.request({
		method: "GET",
		url: `${path}/instances/${instanceId}?validate=true`,
	});

	if (formInstance.data === true) return true;
	else return normaliseForm(formInstance.data);
};

/**
 * Lists all of the form definitions for the tenant
 */
const getDefinitions = () =>
	api.request({ method: "GET", url: `${path}/definitions` });

/**
 * Patch the data of a form instance
 *
 * @param instanceId the unique instance id of the form to update
 * @param data data being patched to the form
 */
const patch = async (
	instanceId: string,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	data: { [k: string]: any },
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		method: "PATCH",
		url: `${path}/instances/${instanceId}`,
		data,
		customOpts,
	});
};

/**
 * Save a form
 *
 * @param instanceId form instance id
 */
const save = async (
	instanceId: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		url: `${path}/instances/${instanceId}`,
		method: "PUT",
		customOpts,
	});
};

/**
 * Close a form instance
 * @param instanceId form instance id
 * @param customOpts custom options
 */
const close = async (
	instanceId: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	return api.request({
		url: `${path}/instances/${instanceId}`,
		method: "DELETE",
		customOpts,
	});
};

/**
 * Gets form Definition Json from forms service
 *
 * @param formId - Id of form
 */
const getFormDefinition = async (formId: string) => {
	return await api.request({
		url: `forms/definitions/${formId}`,
		method: "GET",
	});
};

export default {
	create,
	createChild,
	get,
	validateForm,
	patch,
	save,
	close,
	getDefinitions,
	getFormDefinition,
};
